import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  DataRoomRFICategory,
  FileTypeRFI,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import DocumentInstanceInList from '../DocumentInstanceInList';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { useTranslation } from 'react-i18next';
import confirm from 'modules/confirm';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Loader } from 'ncoded-component-library';
import useRfiCategory from 'hooks/useRfiCategory';
import { getIsAdditionalDocuments } from 'router/subrouters/DataRoom/utils';

import './CategoryInstance.styles.scss';

type CategoryInstanceProps = {
  className?: string;
  dataRoomRFICategory: DataRoomRFICategory;
  onAddFileToDr: (categoryId: string, file: FileTypeRFI) => void;
};

const CategoryInstance: React.FC<CategoryInstanceProps> = (props) => {
  const { className, dataRoomRFICategory, onAddFileToDr } = props;

  const [isProcessing, setIsProcessing] = useState(false);

  const { _id, name, dataRoomRFIId, files } = dataRoomRFICategory;

  const { getCategoryName } = useRfiCategory();

  const { updateRfiFile } = useContext(RfiContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('category-instance', className);

  const { t } = useTranslation();

  const isAdditionalCategory = useMemo(
    () => getIsAdditionalDocuments(name),
    [name],
  );

  const ignoreDocument = useCallback(
    async (document: FileTypeRFI) => {
      if (
        await confirm(
          {
            title: t('ignoreDocumentTitle'),
            content: t('ignoreDocumentDescription', {
              documentName: document.file?.name,
            }),
            type: 'delete',
            submitBtnText: t('confirm'),
            cancelBtnText: t('cancel'),
          },
          true,
        )
      ) {
        try {
          setIsProcessing(true);

          const { data } = await dataRoomApi.rfi.ignoreDocument(
            _id,
            document._id,
          );

          updateRfiFile(dataRoomRFIId, data);
        } catch (e) {
          popServerError(e);
        } finally {
          setIsProcessing(false);
        }
      }
    },
    [dataRoomRFIId, _id, t, updateRfiFile, popServerError],
  );

  const removeDocument = useCallback(
    async (document: FileTypeRFI) => {
      if (
        await confirm(
          {
            title: t('deleteDocumentTitle'),
            content: t('deleteDocumentDescription', {
              documentName: document.file?.name,
            }),
            type: 'delete',
            submitBtnText: t('delete'),
            cancelBtnText: t('cancel'),
          },
          true,
        )
      ) {
        try {
          setIsProcessing(true);

          const { data } = await dataRoomApi.rfi.deleteDocument(
            _id,
            document._id,
          );

          updateRfiFile(dataRoomRFIId, data);
        } catch (e) {
          popServerError(e);
        } finally {
          setIsProcessing(false);
        }
      }
    },
    [dataRoomRFIId, _id, t, updateRfiFile, popServerError],
  );

  if (isAdditionalCategory && !files.length) return null;

  return (
    <div className={classes}>
      {isProcessing && <Loader overlay={false} />}

      <p className="category-instance__name">{getCategoryName(name)}</p>

      {files.length ? (
        files.map((file) => (
          <DocumentInstanceInList
            key={file._id}
            rfiId={dataRoomRFIId}
            document={file}
            onIgnoreDocument={() => ignoreDocument(file)}
            onRemoveDocument={() => removeDocument(file)}
            onAddToDataRoom={() => onAddFileToDr(_id, file)}
            onMoveWithinDataRoom={() => onAddFileToDr(_id, file)}
          />
        ))
      ) : (
        <p className="category-instance__no-files">{t('noFilesAvailable')}</p>
      )}
    </div>
  );
};

export default CategoryInstance;
